import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
} from '@mui/material';
import {
  LocationOn,
  Person,
  Schedule,
  LocalShipping,
  CheckCircle,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom truck icon for drivers (green truck with circle background)
const driverIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj4KICA8Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxOCIgZmlsbD0iIzEwYjk4MSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+CiAgPHBhdGggZmlsbD0id2hpdGUiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDgsIDgpIiBkPSJNMTggMTguNUMxOC44MyAxOC41IDE5LjUgMTcuODMgMTkuNSAxN1MxOC44MyAxNS41IDE4IDE1LjUgMTYuNSAxNi4xNyAxNi41IDE3IDE3LjE3IDE4LjUgMTggMTguNU0xOS41IDkuNUgxN1YxMkgyMS40NkwxOS41IDkuNU02IDE4LjVDNi44MyAxOC41IDcuNSAxNy44MyA3LjUgMTdTNi44MyAxNS41IDYgMTUuNSA0LjUgMTYuMTcgNC41IDE3IDUuMTcgMTguNSA2IDE4LjVNMjAgOEwyMyAxMlYxN0gyMUMyMSAxOC42NiAxOS42NiAyMCAxOCAyMFMxNSAxOC42NiAxNSAxN0g5QzkgMTguNjYgNy42NiAyMCA2IDIwUzMgMTguNjYgMyAxN0gxVjZDMSA0Ljg5IDEuODkgNCAzIDRIMTdWOEgyME0zIDZWMTVIMy43NkM0LjMxIDE0LjM5IDUuMTEgMTQgNiAxNFM3LjY5IDE0LjM5IDguMjQgMTVIMTVWNkgzWiIvPgo8L3N2Zz4=',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

// Custom icons for collection status - matching TodaysCollections
// Blue marker for pending/unassigned
const pendingIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const assignedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMjU2M2ViIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const completedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMTBiOTgxIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Driver-specific icons for assigned collections
// Black marker for Dean Hirst
const deanHirstIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjMDAwMDAwIj48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Orange marker for Nicholas Stradomsky
const nicholasIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjRjk3MzE2Ij48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Pink marker for Jack Jones
const jackJonesIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyOCIgaGVpZ2h0PSIyOCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjRUM0ODk5Ij48cGF0aCBkPSJNMTIgMkM4LjEzIDIgNSA1LjEzIDUgOWMwIDUuMjUgNyAxMyA3IDEzczctNy43NSA3LTEzYzAtMy44Ny0zLjEzLTctNy03em0wIDkuNWMtMS4zOCAwLTIuNS0xLjEyLTIuNS0yLjVzMS4xMi0yLjUgMi41LTIuNSAyLjUgMS4xMiAyLjUgMi41LTEuMTIgMi41LTIuNSAyLjV6Ii8+PC9zdmc+',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

// Driver color mapping
const driverColorMap: { [key: string]: L.Icon } = {
  'dean hirst': deanHirstIcon,
  'nicholas stradomsky': nicholasIcon,
  'jack jones': jackJonesIcon,
};

// Helper function to get the right icon based on status and driver name
const getCollectionIcon = (status: string, driverName?: string) => {
  if (status === 'completed') return completedIcon;
  if (status === 'pending' || !driverName) return pendingIcon;

  // For assigned events, check driver name
  const driverNameLower = driverName.toLowerCase();
  const driverIcon = driverColorMap[driverNameLower];
  if (driverIcon) {
    return driverIcon;
  }

  return assignedIcon; // Default blue for other assigned drivers
};

interface Driver {
  id: number;
  name: string;
  driver_code: string;
  latitude: number;
  longitude: number;
  location_updated_at: string;
  active_orders: number;
  minutes_since_update: number;
  assigned_collections?: number;
  completed_collections?: number;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  delivery_address: string;
  delivery_postcode: string;
  latitude: number;
  longitude: number;
  status: string;
  assigned_driver_id: number;  // Changed to match API field
  driver_name?: string;
}

const DriverTracking: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [allOrders, setAllOrders] = useState<Order[]>([]);  // Store all orders
  const [loading, setLoading] = useState(true);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
    
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [driversRes, eventsRes] = await Promise.all([
        api.get('/users/drivers/locations'),
        api.get(`/collection-events/today`)  // Get today's collection events
      ]);

      // Get events array from response (same as TodaysCollections)
      const todaysEvents = eventsRes.data.events || [];
      
      // Parse driver location data and calculate their collection counts
      const driverData = driversRes.data.map((driver: any) => {
        // Count assigned and completed collections for this driver
        const assignedCount = todaysEvents.filter((event: any) => 
          event.assigned_driver_id === driver.id && 
          (event.status === 'assigned' || event.status === 'in_progress')
        ).length;
        
        const completedCount = todaysEvents.filter((event: any) => 
          event.assigned_driver_id === driver.id && 
          event.status === 'completed'
        ).length;
        
        return {
          ...driver,
          latitude: parseFloat(driver.latitude),
          longitude: parseFloat(driver.longitude),
          assigned_collections: assignedCount,
          completed_collections: completedCount
        };
      });
      setDrivers(driverData);
      
      // Filter collection events with valid coordinates
      const locatedOrders = todaysEvents
        .filter((event: any) => event.delivery_latitude && event.delivery_longitude)
        .map((event: any) => ({
          id: event.id,
          order_number: event.order_number,
          customer_name: `${event.customer_first_name || ''} ${event.customer_last_name || ''}`.trim() || 'Unknown',
          delivery_address: event.delivery_address_1 || '',
          delivery_postcode: event.delivery_postcode,
          latitude: parseFloat(event.delivery_latitude),
          longitude: parseFloat(event.delivery_longitude),
          status: event.status,
          assigned_driver_id: event.assigned_driver_id,  // Use correct field
          driver_name: event.driver_name
        }));
      
      setAllOrders(locatedOrders);  // Store all orders
      setOrders(locatedOrders);      // Initially show all orders
    } catch (error) {
      console.error('Error fetching tracking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocationStatus = (minutes: number) => {
    if (minutes < 5) return { color: 'success', text: 'Live' };
    if (minutes < 15) return { color: 'warning', text: `${minutes}m ago` };
    return { color: 'error', text: `${minutes}m ago` };
  };
  
  const handleDriverClick = (driverId: number) => {
    if (selectedDriverId === driverId) {
      // If clicking the same driver, deselect and show all
      setSelectedDriverId(null);
      setOrders(allOrders);
    } else {
      // Select new driver and filter orders
      setSelectedDriverId(driverId);
      const driverOrders = allOrders.filter(order => order.assigned_driver_id === driverId);
      setOrders(driverOrders);
    }
  };

  if (loading) {
    return <Typography>Loading driver tracking...</Typography>;
  }

  // Use Manchester as the center
  const center: [number, number] = [53.4808, -2.2426];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Live Driver Tracking</Typography>
        <Button 
          variant="contained" 
          sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
          onClick={fetchData}
        >
          Refresh Now
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, height: 600, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Live Map
            </Typography>
            <Box sx={{ flexGrow: 1, width: '100%', position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
              <MapContainer
                center={center as [number, number]}
                zoom={11}
                style={{ height: '100%', width: '100%', position: 'absolute' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {/* Driver markers - show only selected driver or all if none selected */}
                {drivers
                  .filter(driver => !selectedDriverId || driver.id === selectedDriverId)
                  .map((driver) => (
                  <Marker
                    key={`driver-${driver.id}`}
                    position={[driver.latitude, driver.longitude]}
                    icon={driverIcon}
                  >
                    <Tooltip direction="top" offset={[0, -20]} opacity={0.9} permanent>
                      <span style={{ fontWeight: 'bold' }}>{driver.name}</span>
                    </Tooltip>
                    <Popup>
                      <div>
                        <strong>{driver.name}</strong><br />
                        Code: {driver.driver_code}<br />
                        Active Orders: {driver.active_orders}<br />
                        Last Update: {driver.minutes_since_update} minutes ago
                      </div>
                    </Popup>
                  </Marker>
                ))}
                
                {/* Today's collection markers */}
                {orders.map((order) => (
                  <Marker
                    key={`collection-${order.id}`}
                    position={[order.latitude, order.longitude]}
                    icon={getCollectionIcon(order.status, order.driver_name)}
                  >
                    <Popup>
                      <div>
                        <strong>Collection #{order.order_number}</strong><br />
                        Customer: {order.customer_name}<br />
                        Address: {order.delivery_address}<br />
                        Postcode: {order.delivery_postcode}<br />
                        Status: {order.status}<br />
                        {order.driver_name && `Assigned to: ${order.driver_name}`}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
              {/* Map Legend */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  right: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 1,
                  p: 1,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  fontSize: '11px',
                }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                  Driver Key
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#000000', borderRadius: '50%', mr: 0.5 }} />
                  <span>Dean Hirst</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#F97316', borderRadius: '50%', mr: 0.5 }} />
                  <span>Nicholas Stradomsky</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#EC4899', borderRadius: '50%', mr: 0.5 }} />
                  <span>Jack Jones</span>
                </Box>
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'block', mt: 0.5, mb: 0.3 }}>
                  Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.3 }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#2563eb', borderRadius: '50%', mr: 0.5 }} />
                  <span>Pending</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 12, height: 12, backgroundColor: '#10b981', borderRadius: '50%', mr: 0.5 }} />
                  <span>Completed</span>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: 600, overflowY: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Active Drivers ({drivers.length})
              </Typography>
              {selectedDriverId && (
                <Button 
                  size="small" 
                  variant="outlined"
                  onClick={() => {
                    setSelectedDriverId(null);
                    setOrders(allOrders);
                  }}
                >
                  Clear Filter
                </Button>
              )}
            </Box>
            {drivers.length === 0 ? (
              <Typography color="textSecondary">No drivers with location data</Typography>
            ) : (
              drivers.map((driver) => {
                const status = getLocationStatus(driver.minutes_since_update);
                return (
                  <Card 
                    key={driver.id} 
                    sx={{ 
                      mb: 2,
                      cursor: 'pointer',
                      backgroundColor: selectedDriverId === driver.id ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        backgroundColor: selectedDriverId === driver.id ? 'action.selected' : 'action.hover'
                      }
                    }}
                    onClick={() => handleDriverClick(driver.id)}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          <Person sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {driver.name}
                        </Typography>
                        <Chip 
                          label={status.text} 
                          size="small" 
                          color={status.color as any}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary">
                        <LocalShipping sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Today's Assigned Orders: {driver.assigned_collections || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <CheckCircle sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        Today's Completed Collections: {driver.completed_collections || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <LocationOn sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        {Number(driver.latitude).toFixed(6)}, {Number(driver.longitude).toFixed(6)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <Schedule sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        {new Date(driver.location_updated_at).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DriverTracking;